import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/Button';
import { Field, fieldInputClass } from '@/components/Field';
import { Modal } from '@/components/Modal';
import { useBrands } from '@/hooks/use-brands';
import { useCreateCampaign } from '@/hooks/use-campaign-flow';
import { describeError } from '@/lib/describe-error';

/**
 * Creating a campaign.
 *
 * ── WHY THIS IS THREE FIELDS AND NOT TEN ──────────────────────────────────────────
 *
 * Brand, name and subject, because those are the three the API requires and everything else has a
 * better moment later: the audience has its own step, the sender belongs beside the checklist that
 * blocks on it, and the HTML is what the next screen is for. A creation dialog that asks for
 * everything up front is one people abandon, and every field in it is a decision made before the
 * author has seen the thing they are deciding about.
 *
 * The subject is asked for here rather than deferred because the API needs one for the arm it creates,
 * and because a campaign called "Untitled" with no subject is indistinguishable from every other one
 * in the list.
 *
 * ── THE BRAND FIELD DISAPPEARS WHEN THERE IS ONE BRAND ────────────────────────────
 *
 * Most deployments have one. Asking somebody to choose from a list of one is a question with no
 * information in it, so it is preselected and hidden — but it is still SENT, so the API's brand-scope
 * check is doing the same work either way.
 */
export function CreateCampaignDialog({ onClose }: { onClose: () => void }) {
  const brands = useBrands();
  const create = useCreateCampaign();

  const activeBrands = (brands.data?.items ?? []).filter((brand) => brand.isActive);
  const onlyBrand = activeBrands.length === 1 ? activeBrands[0] : undefined;

  const [brandId, setBrandId] = useState('');
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [touched, setTouched] = useState<{ brandId?: boolean; name?: boolean; subject?: boolean }>({});

  const resolvedBrandId = onlyBrand?.id ?? brandId;

  const errors = {
    brandId: resolvedBrandId.length === 0 ? 'Choose a brand.' : undefined,
    name: name.trim().length === 0 ? 'Give the campaign a name.' : undefined,
    subject: subject.trim().length === 0 ? 'Write a subject line.' : undefined,
  };

  const isValid = Object.values(errors).every((error) => error === undefined);

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();

    // Never disabled for invalidity (ui-ux.md § 6) — submitting shows what is wrong and focuses it,
    // which is information a disabled button withholds.
    if (!isValid) {
      setTouched({ brandId: true, name: true, subject: true });

      const firstBad = errors.brandId !== undefined ? 'brand' : errors.name !== undefined ? 'name' : 'subject';
      document.getElementById(firstBad)?.focus();

      return;
    }

    // No onClose here: the mutation navigates into the new campaign on success, which unmounts this.
    // Closing first would flash the list before the workspace.
    create.mutate({ brandId: resolvedBrandId, name: name.trim(), subject: subject.trim() });
  }

  return (
    <Modal
      title="New campaign"
      description="You can change any of this later."
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>

          <Button type="submit" form="create-campaign" variant="primary" disabled={create.isPending}>
            <Plus className="size-4" aria-hidden="true" />
            {create.isPending ? 'Creating…' : 'Create and start writing'}
          </Button>
        </>
      }
    >
      <form id="create-campaign" onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        {create.isError && (
          <p className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-ink" role="alert">
            {describeError(create.error).message}
          </p>
        )}

        {onlyBrand === undefined && (
          <Field
            id="brand"
            label="Brand"
            hint="Decides the sending domain, the footer and whether approval is required."
            error={touched.brandId === true ? errors.brandId : undefined}
          >
            <select
              id="brand"
              value={brandId}
              onChange={(event) => setBrandId(event.target.value)}
              onBlur={() => setTouched((previous) => ({ ...previous, brandId: true }))}
              aria-invalid={touched.brandId === true && errors.brandId !== undefined}
              aria-describedby={errors.brandId === undefined ? 'brand-hint' : 'brand-error'}
              className={fieldInputClass}
            >
              <option value="">Choose a brand…</option>
              {activeBrands.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </select>
          </Field>
        )}

        <Field
          id="name"
          label="Name"
          hint="Internal only — recipients never see this."
          error={touched.name === true ? errors.name : undefined}
        >
          <input
            id="name"
            type="text"
            value={name}
            maxLength={300}
            onChange={(event) => setName(event.target.value)}
            onBlur={() => setTouched((previous) => ({ ...previous, name: true }))}
            aria-invalid={touched.name === true && errors.name !== undefined}
            aria-describedby={errors.name === undefined ? 'name-hint' : 'name-error'}
            placeholder="March newsletter"
            className={fieldInputClass}
          />
        </Field>

        <Field
          id="subject"
          label="Subject line"
          hint="What recipients see in their inbox. Editable later, and the linter will comment on it."
          error={touched.subject === true ? errors.subject : undefined}
        >
          <input
            id="subject"
            type="text"
            value={subject}
            maxLength={500}
            onChange={(event) => setSubject(event.target.value)}
            onBlur={() => setTouched((previous) => ({ ...previous, subject: true }))}
            aria-invalid={touched.subject === true && errors.subject !== undefined}
            aria-describedby={errors.subject === undefined ? 'subject-hint' : 'subject-error'}
            placeholder="Your March update from Hazcheck"
            className={fieldInputClass}
          />
        </Field>

        {brands.isSuccess && activeBrands.length === 0 && (
          <p className="rounded-md bg-warning-bg px-3 py-2 text-sm text-warning-ink" role="alert">
            There are no active brands. An administrator has to add one in Settings before a campaign
            can be created — a campaign needs a brand to know which domain it sends from.
          </p>
        )}
      </form>
    </Modal>
  );
}
